import './wdyr'; // ⚠️ Must be the first import — dev-only re-render analysis
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/globals.css';
import { initAppInsights, trackException } from '@/lib/app-insights';
import AppProvider from '@/app/provider';
import { env, isDev, isMockMode } from '@/config/env';
import { DICTIONARY } from '@/config/dictionary';

// Apply brand color as CSS custom properties on <html> so Chakra and Tailwind
// semantic tokens pick them up before the first paint.
(function applyBrandTokens() {
  const root = document.documentElement;
  // dotenv parsers treat # as a comment delimiter, so the value may arrive
  // without the leading # (e.g. "0ea5e9" instead of "#0ea5e9").
  const raw = env.VITE_BRAND_COLOR;
  const hex = raw.startsWith('#') ? raw : `#${raw}`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  root.style.setProperty('--chakra-colors-primary-500', hex);
  root.style.setProperty('--accent-subtle', `rgba(${String(r)}, ${String(g)}, ${String(b)}, 0.15)`);
})();

// Initialise Azure Application Insights
if (!isDev) {
  initAppInsights();
}

// Start MSW mock service worker in development if mocks are enabled
async function enableMocking(): Promise<void> {
  if (isDev) {
    console.info('[bootstrap] Mock mode:', isMockMode, '| API base URL:', env.VITE_API_BASE_URL);
  }

  if (!isMockMode) return;
  const { worker } = await import('@/testing/mocks/browser');
  await worker.start({
    onUnhandledRequest: (request, print) => {
      const requestUrl = new URL(request.url);
      const isChromeExtensionRequest = requestUrl.protocol === 'chrome-extension:';
      const isSameOriginRequest = requestUrl.origin === window.location.origin;
      const isApiRequest = requestUrl.pathname.startsWith('/api/');

      if (isChromeExtensionRequest) {
        return;
      }

      if (!isSameOriginRequest || !isApiRequest) {
        return;
      }

      print.warning();
    },
  });
}

void enableMocking()
  .then(() => {
    const rootElement = document.getElementById('root');
    if (!rootElement) throw new Error('#root element not found');

    createRoot(rootElement, {
      onCaughtError(error: unknown) {
        trackException(error);
      },
      onUncaughtError(error: unknown) {
        trackException(error);
      },
    }).render(
      <StrictMode>
        <AppProvider />
      </StrictMode>,
    );
  })
  .catch((error: unknown) => {
    if (isDev) console.error('[bootstrap error]', error);
    trackException(error);
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.innerHTML = `
        <div id="root-fallback">
          <h1>${DICTIONARY.errors.pageError}</h1>
          <p>${DICTIONARY.errors.pageErrorDescription}</p>
          <p>${DICTIONARY.errors.contactPrompt} <a href="mailto:${DICTIONARY.errors.contactEmail}">${DICTIONARY.errors.contactEmail}</a></p>
        </div>
      `;
    }
  });
